import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import SearchInput from "../../../../core/Inputs/SearchInput";
import Svg from "../../../../component/common/Svg";
import { useDispatch } from "react-redux";
import { getTokenData } from "../../../../store/actions/authAction";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import moment from "moment";
import SurveyGroupModal from "../../../../component/common/modals/SurveyGroupModal";
import RenameGroupModal from "../../../../component/common/modals/RenameGroupModal";
import { getSurveyList } from "../../../../store/actions/surveyAction";
import {
  getSurveyGroupDetail,
  updateSurveyGroup,
  updateSurveyGroupItems,
  removeSurveyFromGroup,
  addSurveyToGroup,
} from "../../../../store/actions/surveyGroupAction";
import { toast } from "react-toastify";
import Card from "../../../../core/Card";
import style from "../../../style.module.css";
import GroupUtmVariables from "./GroupUtmVariables";

export default function GroupSurveyList() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [userId, setUserId] = useState("");
  const [filters, setFilters] = useState({ search: "", status: "" });
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortableSurveys, setSortableSurveys] = useState([]);
  const [primarySurveyId, setPrimarySurveyId] = useState(null);
  const [isAddSurveyModalOpen, setIsAddSurveyModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [removingSurvey, setRemovingSurvey] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Tab state - get from URL query param or default to 'surveys'
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(
    queryParams.get("tab") || "surveys"
  );

  const { surveyData: surveyList } = useSelector((state) => state.surveyData);
  const { groupDetail, groupItems, groupDetailLoading } = useSelector(
    (state) => state.surveyGroupData
  );

  // Group data from Redux
  const groupData = groupDetail
    ? {
        id: groupDetail.su_group_id,
        name: groupDetail.su_group_title,
        status: 1, // Default to Published
      }
    : {
        id: groupId,
        name: t("survey.groups.loading"),
        status: 1,
      };

  useEffect(() => {
    dispatch(getTokenData(setUserId));
  }, [dispatch]);

  // Fetch group details on mount
  useEffect(() => {
    if (groupId) {
      dispatch(getSurveyGroupDetail(groupId));
    }
  }, [groupId, dispatch]);

  // Update sortable surveys and primary when groupItems changes
  useEffect(() => {
    if (groupItems && groupItems.length > 0) {
      const mappedSurveys = groupItems.map((item) => ({
        survey_id: item.su_survey_id,
        survey_name: item.survey_name,
        totalSection: item.totalSection ?? 0,
        totalQuestion: item.totalQuestion ?? 0,
        totalCompletedResponse: item.totalCompletedResponse ?? 0,
        totalResponse: item.totalResponse ?? 0,
        language_name: item.language_name ?? "",
        status: item.status ?? 1,
        team_name: item.team_name ?? "",
        added_on: item.added_on ?? "",
        addedByName: item.addedByName ?? "",
        published_on: item.published_on
          ? moment(item.published_on, "YYYY-MM-DD HH:mm:ss").format(
              "DD/MM/YYYY"
            )
          : "-",
        last_modified: item.last_modified ? new Date(item.last_modified) : null,
        isPrimary: item.su_is_primary === 1,
      }));

      setSortableSurveys(mappedSurveys);

      const primary = mappedSurveys.find((s) => s.isPrimary);
      if (primary) {
        setPrimarySurveyId(primary.survey_id);
      } else {
        setPrimarySurveyId(null);
      }
    } else {
      // Clear surveys and primary when group is empty
      setSortableSurveys([]);
      setPrimarySurveyId(null);
    }
  }, [groupItems]);

  // Fetch surveys for the modal when it opens
  useEffect(() => {
    if (isAddSurveyModalOpen && userId) {
      const payload = {
        user_id: userId,
        perPage: 1000, // Get all surveys for selection
        page: 1,
        sort: "DESC",
        sortby: "su_survey_modified_date_time",
        search: "",
        status: "",
        su_languages: "",
      };
      dispatch(getSurveyList(payload));
    }
  }, [isAddSurveyModalOpen, userId, dispatch]);

  // Get survey name for display
  const getSurveyName = useCallback((survey) => {
    try {
      const surveyNameObj = survey.survey_name
        ? typeof survey.survey_name === "string"
          ? JSON.parse(survey.survey_name)
          : survey.survey_name
        : {};
      return Object.values(surveyNameObj)[0] || t("survey.groups.untitledSurvey");
    } catch (error) {
      return t("survey.groups.untitledSurvey");
    }
  }, [t]);

  // Filter surveys based on search and status
  const filteredSurveys = useMemo(() => {
    return sortableSurveys.filter((survey) => {
      const matchesSearch =
        !filters.search ||
        getSurveyName(survey)
          .toLowerCase()
          .includes(filters.search.toLowerCase());
      const matchesStatus = !filters.status || survey.status === filters.status;
      return matchesSearch && matchesStatus;
    });
  }, [sortableSurveys, filters.search, filters.status, getSurveyName]);

  // Filter available surveys (exclude surveys already in the group)
  const availableSurveys = useMemo(() => {
    if (!surveyList || !groupItems) return surveyList || [];

    const groupSurveyIds = groupItems.map((item) => item.su_survey_id);
    return surveyList.filter(
      (survey) => !groupSurveyIds.includes(survey.survey_id)
    );
  }, [surveyList, groupItems]);

  // Handle reordering surveys via drag and drop
  const handleReorder = useCallback(
    (fromIndex, toIndex) => {
      if (fromIndex === toIndex) return;

      // Create new array with reordered items
      const newSurveys = Array.from(filteredSurveys);
      const [movedItem] = newSurveys.splice(fromIndex, 1);
      newSurveys.splice(toIndex, 0, movedItem);

      // Update local state immediately for smooth UI
      setSortableSurveys(newSurveys);

      // Prepare items array with updated sort orders
      const updatedItems = newSurveys
        .map((survey, index) => {
          const groupItem = groupItems.find(
            (item) => item.su_survey_id === survey.survey_id
          );
          if (!groupItem) return null;

          return {
            su_group_item_id: groupItem.su_group_item_id,
            su_survey_id: survey.survey_id,
            su_language_code: groupItem.su_language_code,
            su_sort_order: index + 1, // New sort order based on position
            su_is_primary: groupItem.su_is_primary,
            su_is_active: groupItem.su_is_active,
          };
        })
        .filter((item) => item !== null);

      // Call API to persist the new order
      dispatch(
        updateSurveyGroupItems(
          {
            group_id: groupId,
            items: updatedItems,
          },
          () => {
            dispatch(getSurveyGroupDetail(groupId));
            toast.success(t("survey.groups.surveyOrderUpdated"));
          },
          (error) => {
            const backendMsg =
              error.response?.data?.msg || t("survey.groups.toast.deleteError");
            toast.error(backendMsg);
            // Revert to original order on error
            dispatch(getSurveyGroupDetail(groupId));
          }
        )
      );
    },
    [filteredSurveys, groupItems, groupId, dispatch, t]
  );

  // Add drag handle icon and functionality to table rows
  useEffect(() => {
    const tableBody = document.querySelector(".rdt_TableBody");
    if (!tableBody || !filteredSurveys || filteredSurveys.length === 0) return;

    const rows = tableBody.querySelectorAll(".rdt_TableRow");

    rows.forEach((row, index) => {
      const checkboxCell = row.querySelector(".rdt_TableCell:first-child");
      let dragHandle = checkboxCell?.querySelector(".drag-handle-icon");

      // Add drag handle icon if not already present
      if (checkboxCell && !dragHandle) {
        dragHandle = document.createElement("div");
        dragHandle.className = "drag-handle-icon";
        dragHandle.style.cursor = "grab";
        dragHandle.draggable = true;
        dragHandle.innerHTML =
          '<svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="4.8" height="4.8" fill="#D2D2D6"/><rect x="7.19995" width="4.8" height="4.8" fill="#D2D2D6"/><rect y="7.19995" width="4.8" height="4.8" fill="#D2D2D6"/><rect x="7.19995" y="7.19995" width="4.8" height="4.8" fill="#D2D2D6"/><rect y="14.3999" width="4.8" height="4.8" fill="#D2D2D6"/><rect x="7.19995" y="14.3999" width="4.8" height="4.8" fill="#D2D2D6"/></svg>';
        checkboxCell.insertBefore(dragHandle, checkboxCell.firstChild);
      }

      // Make drag handle draggable
      if (dragHandle) {
        dragHandle.draggable = true;
        dragHandle.style.cursor = "grab";

        // Drag start
        dragHandle.ondragstart = (e) => {
          e.stopPropagation();
          dragHandle.style.cursor = "grabbing";
          setDraggedIndex(index);
          row.style.opacity = "0.5";
          e.dataTransfer.effectAllowed = "move";
        };

        // Drag end
        dragHandle.ondragend = (e) => {
          e.stopPropagation();
          dragHandle.style.cursor = "grab";
          row.style.opacity = "1";
          setDraggedIndex(null);

          // Remove all drag-over classes
          rows.forEach((r) => r.classList.remove("drag-over"));
        };
      }

      // Row drag over
      row.ondragover = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (draggedIndex !== null && draggedIndex !== index) {
          row.classList.add("drag-over");
        }
      };

      // Row drag leave
      row.ondragleave = (e) => {
        e.stopPropagation();
        row.classList.remove("drag-over");
      };

      // Row drop
      row.ondrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        row.classList.remove("drag-over");

        if (draggedIndex !== null && draggedIndex !== index) {
          handleReorder(draggedIndex, index);
        }
      };
    });
  }, [filteredSurveys, draggedIndex, handleReorder]);

  const handleRenameGroup = (data) => {
    dispatch(
      updateSurveyGroup(
        {
          group_id: data.id,
          group_title: data.name,
        },
        () => {
          dispatch(getSurveyGroupDetail(groupId));
          toast.success(t("survey.groups.toast.renamed"));
          setIsRenameModalOpen(false);
        },
        (error) => {
          const backendMsg =
            error.response?.data?.msg || t("survey.groups.toast.renameError");
          toast.error(backendMsg);
        }
      )
    );
  };

  const handleSetPrimary = (surveyId) => {
    // Find the item details from groupItems
    const targetItem = groupItems.find(
      (item) => item.su_survey_id === surveyId
    );
    if (!targetItem) return;

    // Create items array with updated primary status
    const items = groupItems.map((item) => ({
      su_group_item_id: item.su_group_item_id,
      su_survey_id: item.su_survey_id,
      su_language_code: item.su_language_code,
      su_sort_order: item.su_sort_order,
      su_is_primary: item.su_survey_id === surveyId ? 1 : 0,
      su_is_active: item.su_is_active,
    }));

    dispatch(
      updateSurveyGroupItems(
        {
          group_id: groupId,
          items: items,
        },
        () => {
          dispatch(getSurveyGroupDetail(groupId));
          toast.success(t("survey.groups.primarySurveyUpdated"));
        },
        (error) => {
          const backendMsg =
            error.response?.data?.msg || t("survey.groups.toast.deleteError");
          toast.error(backendMsg);
        }
      )
    );
  };

  const handleRemoveSurvey = (surveyId) => {
    const survey = sortableSurveys.find((s) => s.survey_id === surveyId);
    if (survey) {
      setRemovingSurvey({
        id: surveyId,
        name: getSurveyName(survey),
      });
      setIsRemoveModalOpen(true);
    }
  };

  const confirmRemoveSurvey = () => {
    if (!removingSurvey) return;

    dispatch(
      removeSurveyFromGroup(
        {
          group_id: groupId,
          survey_id: removingSurvey.id,
        },
        () => {
          dispatch(getSurveyGroupDetail(groupId));
          toast.success(t("survey.groups.surveyRemovedSuccess"));
          setIsRemoveModalOpen(false);
          setRemovingSurvey(null);
        },
        (error) => {
          const backendMsg =
            error.response?.data?.msg || t("survey.groups.toast.deleteError");
          toast.error(backendMsg);
          setIsRemoveModalOpen(false);
          setRemovingSurvey(null);
        }
      )
    );
  };

  const handleSearch = (value) => {
    setFilters({ ...filters, search: value });
  };

  // Column definitions
  const columns = [
    {
      name: t("survey.surveyName"),
      width: "180px",
      cell: (row) => {
        const surveyName = getSurveyName(row);
        const isPrimary = primarySurveyId === row.survey_id;
        return (
          <div className="w-100">
            <div className="d-flex align-items-center gap-2">
              <span className="surv-title">{surveyName}</span>
              {isPrimary && (
                <span className="badge badge-primary-custom">{t("survey.groups.primary")}</span>
              )}
            </div>
            <div className="secondary-text">
              {row.totalSection} {t("survey.groups.sections")}, {row.totalQuestion} {t("survey.groups.questions")}
            </div>
          </div>
        );
      },
    },
    {
      name: t("survey.completionResponses"),
      selector: (row) => row.totalCompletedResponse,
      cell: (row) => (
        <div className="w-100 response-complete">
          {row.totalCompletedResponse ?? ""}/{row.totalResponse ?? ""}
        </div>
      ),
    },
    {
      name: t("survey.language"),
      selector: (row) => row.language_name,
      cell: (row) => (
        <div className="w-100 response-complete">{row.language_name ?? ""}</div>
      ),
    },
    {
      name: t("survey.status"),
      selector: (row) => row.status,
      cell: (row) => {
        return (
          <div className="w-100">
            <span
              className={`table-status ${
                Number(row.status) === 1
                  ? "Active"
                  : Number(row.status) === 0
                  ? "Inactive"
                  : "Draft"
              }`}
            >
              {Number(row.status) === 1
                ? t("survey.published")
                : Number(row.status) === 0
                ? "Pending"
                : t("survey.draft")}
            </span>
          </div>
        );
      },
    },
    {
      name: t("webhook.assign"),
      selector: (row) => row.team_name,
      cell: (row) => (
        <div className="w-100 response-complete">{row.team_name ?? ""}</div>
      ),
    },
    {
      name: t("survey.addedOnAddedBy"),
      selector: (row) => row.added_on,
      cell: (row) => {
        let formattedAddedOn = "";
        if (row.added_on) {
          formattedAddedOn = moment(
            row.added_on,
            "DD-MM-YYYY | hh:mm A"
          ).format("DD-MM-YYYY");
        }
        return (
          <div className="w-100">
            {formattedAddedOn || "-"}
            {row.addedByName && (
              <div className="secondary-text bold">{row.addedByName}</div>
            )}
          </div>
        );
      },
    },
    {
      name: t("survey.groups.responses"),
      selector: (row) => row.totalResponse,
      cell: (row) => <div className="w-100">{row.totalResponse ?? 0}</div>,
    },
    {
      name: t("survey.publishedOn"),
      selector: (row) => row.published_on,
      cell: (row) => {
        return <div className="w-100">{row.published_on || "-"}</div>;
      },
    },
    {
      name: t("survey.customVariable.action"),
      width: "85px",
      cell: (row) => (
        <div className="dropdown table-dropdown">
          <button
            className="btn dropdown-tgl"
            type="button"
            id="viewoptions"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            <Svg name="action" />
          </button>
          <div className="dropdown-menu" aria-labelledby="viewoptions">
            {/* Only show "Make it Primary" if this survey is NOT already primary */}
            {!row.isPrimary && (
              <span
                className="dropdown-item d-flex align-items-center"
                onClick={() => handleSetPrimary(row.survey_id)}
                style={{ cursor: "pointer" }}
              >
                <div className="icon-circle me-3">
                  <Svg name="star-dropdown" />
                </div>
                <span> {t('survey.groups.makePrimary')}</span>
              </span>
            )}

            <span
              className="dropdown-item d-flex align-items-center"
              onClick={() =>
                navigate(`/admin/analytics/leads?survey_id=${row.survey_id}`)
              }
              style={{ cursor: "pointer" }}
            >
              <div className="icon-circle me-3">
                <Svg name="viewReport" />
              </div>
              <span>{t('survey.actions.viewLeads')}</span>
            </span>

            <span
              className="dropdown-item d-flex align-items-center"
              onClick={() => handleRemoveSurvey(row.survey_id)}
              style={{ cursor: "pointer" }}
            >
              <div className="icon-circle me-3">
                <Svg name="minus-dropdown" />
              </div>
              <span> {t('survey.groups.removeGroup')}</span>
            </span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex-grow-1 dashboard-page-outer">
      <div className="dashboard-page-wrap">
        {/* Breadcrumbs */}
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/admin/survey/groups">{t("survey.groups.title")}</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              <strong>{groupData.name}</strong>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="page-heading d-md-flex align-items-center justify-content-between mb-20">
          <div className="head-lef">
            <div className="d-flex align-items-center gap-2 mb-2">
              <button
                className="btn btn-link p-0"
                onClick={() => navigate("/admin/survey/groups")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={20}
                  height={20}
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M9.99984 15.8334L4.1665 10L9.99984 4.16669"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15.8332 10H4.1665"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <h3 className="mb-0 group-title">{groupData.name}</h3>
              {/* <span className={`table-status ${Number(groupData.status) === 1 ? 'Active' : 'Draft'}`}>
                                {Number(groupData.status) === 1 ? t('survey.published') : t('survey.draft')}
                            </span> */}
            </div>
          </div>
          <div className="right-wrap mt-2 mt-md-0 d-flex align-items-center gap-2">
            {/* Group actions dropdown */}

            <button
              className="btn btn-primary add-btn"
              onClick={() => setIsAddSurveyModalOpen(true)}
            >
              <Svg name="plus" />
              {t('survey.groups.selectSurvey')}
            </button>

            <div className="dropdown">
              <button
                className="btn btn-link p-0"
                type="button"
                id="groupActions"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <Svg name="action-v" />
              </button>
              <div className="dropdown-menu" aria-labelledby="groupActions">
                <span
                  className="dropdown-item d-flex align-items-center"
                  onClick={() => setIsRenameModalOpen(true)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="icon-circle me-3">
                    <Svg name="Edit-Survey" />
                  </div>
                  <span> {t('survey.groups.rename')}</span>
                </span>
                <Link
                  to={`/admin/survey/groups/${groupId}/share`}
                  className="dropdown-item d-flex align-items-center"
                >
                  <div className="icon-circle me-3">
                    <Svg name="share" />
                  </div>
                  <span>{t('survey.groups.share')}</span>
                </Link>
                {/* <span className="dropdown-item d-flex align-items-center">
                                    <div className="icon-circle me-3">
                                        <Svg name="delete" />
                                    </div>
                                    <span>Delete Group</span>
                                </span> */}
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation mb-3 mt-4">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "surveys" ? "active" : ""
                }`}
                onClick={() => {
                  setActiveTab("surveys");
                  navigate(`/admin/survey/groups/${groupId}?tab=surveys`);
                }}
              >
                 {t('survey.title')}
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "utm" ? "active" : ""}`}
                onClick={() => {
                  setActiveTab("utm");
                  navigate(`/admin/survey/groups/${groupId}?tab=utm`);
                }}
              >
                {t('survey.utm.title')}
              </button>
            </li>
          </ul>
        </div>

        {/* Conditional Content Based on Active Tab */}
        {activeTab === "surveys" ? (
          <>
            {/* Search and Filters */}
            <div className="table-wrapper-view">
              <div className="search-filter d-md-flex align-items-center surveyfilter">
                <div className="flex-grow-1 search-filter-padding">
                  <SearchInput
                    className="custom-search-input"
                    type="def"
                    view="outlined"
                    color="primary"
                    placeholder={t('survey.search')}
                    fill={true}
                    onSearch={handleSearch}
                    onClear={() => setFilters({ ...filters, search: "" })}
                  />
                </div>
                {/* <div className="textS d-flex align-items-center">
                            <label className="text-label">{t('survey.status')}:</label>
                            <div className="dropdown table-dropdown ques-options">
                                <button className="btn dropdown-tgl" type="button" id="quesoptions" data-bs-toggle="dropdown"
                                    aria-haspopup="true" aria-expanded="false">
                                    {status ? status.name : t('survey.allL')}
                                </button>
                                <div className="dropdown-menu" aria-labelledby="quesoptions">
                                    <span className="dropdown-item" onClick={() => handleClickDropDown(t('survey.allL'))}>
                                        {t('survey.allL')}
                                    </span>
                                    {surveyStatus?.map((opt, index) => {
                                        return (
                                            <span
                                                className="dropdown-item"
                                                key={index}
                                                onClick={() => handleClickDropDown(opt)}
                                            >
                                                {opt.name}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        </div> */}
              </div>

              {/* DataTable */}
              <div className="tablePage bg-white pl-30">
                <div className="content-table survey-table">
                  <div className="data-table-wrap custom-data-table">
                    <DataTable
                      title=""
                      columns={columns}
                      data={filteredSurveys}
                      pagination
                      paginationServer={false}
                      progressPending={groupDetailLoading}
                      //selectableRows
                      selectableRowsHighlight
                      onSelectedRowsChange={(state) => {
                        setSelectedRows(state.selectedRows);
                      }}
                      conditionalRowStyles={[
                        {
                          when: (row) => {
                            return selectedRows.some(
                              (selectedRow) =>
                                selectedRow.survey_id === row.survey_id
                            );
                          },
                          classNames: ["row-selected"],
                        },
                      ]}
                      noDataComponent={
                        <div className="text-center py-4 text-muted">
                          {t("survey.groups.noSurveysInGroup")}
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : activeTab === "utm" ? (
          <GroupUtmVariables />
        ) : null}
      </div>

      {/* Add Survey Modal */}
      <SurveyGroupModal
        open={isAddSurveyModalOpen}
        onSuccess={async (modalData) => {
          if (!modalData.surveyIds || modalData.surveyIds.length === 0) {
            toast.error(t("survey.groups.pleaseSelectSurvey"));
            return;
          }

          // Get the current max sort order
          const maxSortOrder =
            groupItems.length > 0
              ? Math.max(...groupItems.map((item) => item.su_sort_order || 0))
              : 0;

          const results = {
            success: 0,
            errors: [],
            languageConflicts: [],
          };

          // Add each selected survey to the group
          for (let i = 0; i < modalData.surveyIds.length; i++) {
            const surveyId = modalData.surveyIds[i];
            const survey = availableSurveys.find(
              (s) => s.survey_id === surveyId
            );

            if (!survey) continue;

            // Get survey name for better error messages
            let surveyName = t("survey.groups.unknownSurvey");
            try {
              const surveyNameObj = survey.survey_name
                ? JSON.parse(survey.survey_name)
                : {};
              const languageCode = survey.su_languages?.split(",")[0];
              surveyName =
                surveyNameObj[languageCode] ||
                Object.values(surveyNameObj)[0] ||
                t("survey.groups.unknownSurvey");
            } catch (e) {
              // Use fallback name
            }

            // Get language code - use the first language from su_languages
            const languageCode = survey.su_languages
              ? survey.su_languages.toString().split(",")[0].trim()
              : "";

            if (!languageCode) {
              results.errors.push(`${surveyName}: ${t("survey.groups.noLanguageCode")}`);
              continue;
            }

            // Prepare payload for this survey
            const payload = {
              group_id: groupId,
              survey_id: surveyId,
              language_code: languageCode,
              sort_order: maxSortOrder + i + 1,
              is_primary: 0, // New surveys are not primary by default
            };

            try {
              await new Promise((resolve, reject) => {
                dispatch(
                  addSurveyToGroup(
                    payload,
                    () => {
                      results.success++;
                      resolve();
                    },
                    (error) => {
                      const msg =
                        error.response?.data?.msg || t("survey.groups.toast.createError");

                      // Check if it's a language conflict
                      if (
                        msg.toLowerCase().includes("language already exists") ||
                        (msg.toLowerCase().includes("language") &&
                          msg.toLowerCase().includes("group"))
                      ) {
                        results.languageConflicts.push(
                          `${surveyName} (${languageCode})`
                        );
                      } else {
                        results.errors.push(`${surveyName}: ${msg}`);
                      }
                      reject(error);
                    }
                  )
                );
              });
            } catch (err) {
              // Error already handled in the callback
            }
          }

          // Show results
          if (results.success > 0) {
            toast.success(
              `${results.success} ${t("survey.groups.surveysAddedSuccess")}`
            );
            // Refresh the group details
            dispatch(getSurveyGroupDetail(groupId));
          }

          if (results.languageConflicts.length > 0) {
            toast.warning(
              t("survey.groups.cannotAddDuplicateLanguages")
            );
          }

          if (results.errors.length > 0) {
            toast.error(
              `${t("survey.groups.failedToAddSurveys")}: ${results.errors[0] || ""}`
            );
          }

          setIsAddSurveyModalOpen(false);
        }}
        onCancel={() => {
          setIsAddSurveyModalOpen(false);
        }}
        groupData={groupData}
        surveysList={availableSurveys}
        selectedSurveyIds={[]} // Empty array as we're adding new surveys
        mode="addToGroup"
      />

      {/* Rename Group Modal */}
      <RenameGroupModal
        open={isRenameModalOpen}
        onSuccess={handleRenameGroup}
        onCancel={() => setIsRenameModalOpen(false)}
        groupData={groupData}
      />

      {/* Remove Survey from Group Modal */}
      <div
        className={`customvar-modal modal custom-modal fade deleteModal ${
          isRemoveModalOpen ? "show" : ""
        }`}
        style={{ display: `${isRemoveModalOpen ? "block" : "none"}` }}
        id="removeSurveyModal"
        tabIndex={-1}
        aria-labelledby="removeSurveyModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className={"formcontainer " + style.container}>
                <div
                  className={
                    "formvawrapper delete-modal-wrap modal-setting-medium " +
                    style.wrapper
                  }
                >
                  <Card view="raised" className={"formstyle p-0 " + style.card}>
                    <div className="delete-body-wrap delete-language">
                      <div className="heading">
                        <h3>{t("survey.groups.removeSurvey")}</h3>
                        <p>
                          {t("survey.groups.removeSurveyConfirm")}
                        </p>
                      </div>
                      <div className="warning-message">
                        <div className="title d-flex align-items-center">
                          <svg
                            width={40}
                            height={42}
                            viewBox="0 0 40 42"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M20 2L37.3205 32H2.67949L20 2Z"
                              stroke="#F05454"
                              strokeWidth={3}
                              strokeLinejoin="round"
                            />
                            <path
                              d="M20 13.5385V22"
                              stroke="#F05454"
                              strokeWidth={3}
                            />
                            <circle
                              cx="19.9994"
                              cy="25.8461"
                              r={1}
                              fill="black"
                              stroke="#F05454"
                              strokeWidth="1.07692"
                            />
                          </svg>
                          <h4>{t("survey.groups.deleteWarning")}</h4>
                        </div>
                        <p>
                          {t("survey.groups.deleteMessage")} <strong>{removingSurvey?.name}</strong> {t("survey.groups.removeSurveyWarning")}
                        </p>
                      </div>
                      <div className="button-wrap d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-bordered color-delete"
                          onClick={() => {
                            setIsRemoveModalOpen(false);
                            setRemovingSurvey(null);
                          }}
                        >
                         {t('survey.customVariable.cancel')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-delete ms-3"
                          onClick={confirmRemoveSurvey}
                        >
                       {t("survey.actions.delete")}
                        </button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Backdrop for Remove Survey */}
      <div
        className={`modal-backdrop fade ${isRemoveModalOpen ? "show" : ""}`}
        style={{ display: `${isRemoveModalOpen ? "block" : "none"}` }}
        onClick={() => {
          setIsRemoveModalOpen(false);
          setRemovingSurvey(null);
        }}
      ></div>
    </div>
  );
}
